import { ENTITIES, compareOrder, isAlive, isInTrash } from '@nedelnik/shared';
import { create } from 'zustand';

import type { MergeResult } from '../core/db/local.js';
import type { StoredRecord } from '../core/db/schema.js';
import type { Identity } from '../core/auth/session.js';
import type { SyncStatus } from '../core/sync/status.js';
import type {
  ChecklistItem,
  Entity,
  Settings,
  SyncRecord,
  Task,
  TaskLink,
  TaskTag,
} from '@nedelnik/shared';

/**
 * Зеркало локальной копии в памяти и реактивные выборки над ним.
 *
 * IndexedDB — источник правды, но перерисовывать интерфейс, каждый раз ходя в
 * неё, незачем: копия целиком помещается в памяти, а выборки должны быть
 * синхронными. Записи входят сюда только после слияния, поэтому расхождение
 * между зеркалом и базой невозможно по построению.
 */

export type RecordMaps = Record<Entity, Map<string, SyncRecord>>;

export interface AppState {
  ready: boolean;
  identity: Identity | undefined;
  status: SyncStatus;
  records: RecordMaps;
  setReady(ready: boolean): void;
  setIdentity(identity: Identity | undefined): void;
  setStatus(status: SyncStatus): void;
  hydrate(stored: StoredRecord[]): void;
  applyChanges(changes: MergeResult[]): void;
  /** Забыть записи: их правку сервер не принял, и своей версии у нас больше нет. */
  removeRecords(keys: { entity: Entity; key: string }[]): void;
  reset(): void;
}

function emptyRecords(): RecordMaps {
  return Object.fromEntries(ENTITIES.map((entity) => [entity, new Map()])) as RecordMaps;
}

export const useAppStore = create<AppState>()((set) => ({
  ready: false,
  identity: undefined,
  status: { kind: 'saved' },
  records: emptyRecords(),

  setReady: (ready) => set({ ready }),
  setIdentity: (identity) => set({ identity }),
  setStatus: (status) => set({ status }),

  hydrate: (stored) =>
    set(() => {
      const records = emptyRecords();
      for (const item of stored) {
        records[item.entity].set(item.key, item.record);
      }
      return { records };
    }),

  // Меняется только карта тронутой сущности: подписчики на остальные не должны
  // перерисовываться из-за чужой правки.
  applyChanges: (changes) =>
    set((state) => {
      const records = { ...state.records };
      const touched = new Set<Entity>();

      for (const change of changes) {
        if (!touched.has(change.entity)) {
          records[change.entity] = new Map(records[change.entity]);
          touched.add(change.entity);
        }
        records[change.entity].set(change.key, change.record);
      }

      return { records };
    }),

  removeRecords: (keys) =>
    set((state) => {
      const records = { ...state.records };
      const touched = new Set<Entity>();

      for (const item of keys) {
        if (!touched.has(item.entity)) {
          records[item.entity] = new Map(records[item.entity]);
          touched.add(item.entity);
        }
        records[item.entity].delete(item.key);
      }

      return { records };
    }),

  reset: () => set({ records: emptyRecords(), identity: undefined, ready: false }),
}));

// --------------------------------------------------------------------------
// Выборки
// --------------------------------------------------------------------------

/**
 * Живые задачи: надгробия наружу не отдаются никогда.
 *
 * Функция чистая и принимает состояние, а не подписывается на него. Отдавать
 * такую выборку прямо в `useAppStore` нельзя: она собирает новый массив на
 * каждый вызов, а `useSyncExternalStore` под zustand сравнивает результат по
 * ссылке — вечно «новое» состояние уводит React в бесконечную перерисовку и
 * гасит страницу. Подписываться надо на карту записей, а выборку считать
 * через `useMemo` (см. `useTasks`).
 */
export function selectTasks(state: AppState): Task[] {
  return [...state.records.task.values()].filter(isAlive) as Task[];
}

/** Карта задач: её ссылка меняется только при настоящей правке. */
export function selectTaskMap(state: AppState): Map<string, SyncRecord> {
  return state.records.task;
}

/** Живые задачи из карты — результат кэшируется по ссылке на карту. */
export function tasksOf(map: Map<string, SyncRecord>): Task[] {
  return [...map.values()].filter(isAlive) as Task[];
}

/**
 * Задачи в корзине: надгробия, которым ещё не вышел срок (3.18.6).
 *
 * Это единственное место, где надгробия отдаются наружу, и потому оно
 * отдельное: везде остальное `tasksOf` обязано их скрывать, иначе удалённая
 * задача вернётся в спринт, в поиск и в счётчики.
 *
 * Порядок — от свежих к старым: возвращают обычно то, что удалили только что.
 */
export function trashedOf(map: Map<string, SyncRecord>, now: Date): Task[] {
  return ([...map.values()].filter((record) => isInTrash(record, now)) as Task[]).sort((a, b) =>
    (b.deleted_at ?? '').localeCompare(a.deleted_at ?? ''),
  );
}

/** Карта тегов: подписка идёт на неё по той же причине, что и у задач. */
export function selectTagMap(state: AppState): Map<string, SyncRecord> {
  return state.records.task_tag;
}

/**
 * Живые теги, разложенные по задачам. Тег — отдельная запись, а не поле задачи
 * (раздел 3.3 архитектуры), поэтому связь собирается здесь, а не приезжает
 * готовой.
 */
export function tagsByTask(map: Map<string, SyncRecord>): Map<string, string[]> {
  const grouped = new Map<string, string[]>();

  for (const record of map.values()) {
    if (!isAlive(record)) continue;

    const taskId = record['task_id'];
    const tag = record['tag'];
    if (typeof taskId !== 'string' || typeof tag !== 'string' || tag.length === 0) continue;

    const known = grouped.get(taskId);
    if (known === undefined) {
      grouped.set(taskId, [tag]);
    } else if (!known.includes(tag)) {
      known.push(tag);
    }
  }

  for (const tags of grouped.values()) tags.sort();
  return grouped;
}

/**
 * Те же теги, но записями: снять тег — значит поставить надгробие конкретной
 * записи, а по одному только тексту её не найти (две записи с одним текстом
 * возможны после конкурентного добавления с двух устройств).
 */
export function tagRecordsByTask(map: Map<string, SyncRecord>): Map<string, TaskTag[]> {
  const grouped = new Map<string, TaskTag[]>();

  for (const record of map.values()) {
    if (!isAlive(record)) continue;

    const taskId = record['task_id'];
    if (typeof taskId !== 'string' || typeof record['tag'] !== 'string') continue;

    const known = grouped.get(taskId);
    if (known === undefined) grouped.set(taskId, [record as TaskTag]);
    else known.push(record as TaskTag);
  }

  for (const tags of grouped.values()) {
    tags.sort((a, b) =>
      a.tag === b.tag ? a.id.localeCompare(b.id) : (a.tag ?? '') < (b.tag ?? '') ? -1 : 1,
    );
  }
  return grouped;
}

/**
 * Все записи тегов по задачам, **включая снятые**.
 *
 * Нужны при навешивании тега: пара «задача и тег» уникальна в базе вместе с
 * надгробиями (`task_tags_task_bidx_uniq` в 0003_crypto.sql, индекс не
 * частичный). Значит снятый тег нельзя вернуть новой записью — сервер отвергнет
 * её как дубль, правка уедет в карантин, а на экране останется «правки не
 * приняты сервером». Показывать снятые теги, разумеется, нельзя — для этого
 * есть `tagRecordsByTask`.
 */
export function allTagRecordsByTask(map: Map<string, SyncRecord>): Map<string, TaskTag[]> {
  const grouped = new Map<string, TaskTag[]>();

  for (const record of map.values()) {
    const taskId = record['task_id'];
    if (typeof taskId !== 'string' || typeof record['tag'] !== 'string') continue;

    const known = grouped.get(taskId);
    if (known === undefined) grouped.set(taskId, [record as TaskTag]);
    else known.push(record as TaskTag);
  }

  return grouped;
}

/** Карта связей: подписка идёт на неё по той же причине, что и у задач. */
export function selectLinkMap(state: AppState): Map<string, SyncRecord> {
  return state.records.task_link;
}

/**
 * Живые связи одним списком.
 *
 * Связь хранится в одну сторону (`blocker → blocked`), «зависит от» выводится
 * обратным проходом — решение прототипа, оно же в разделе 3.3 архитектуры.
 * Поэтому раскладывать связи по задачам заранее нельзя: одна и та же запись
 * нужна обеим сторонам, и группировка по одной из них потеряла бы вторую.
 */
export function linksOf(map: Map<string, SyncRecord>): TaskLink[] {
  return [...map.values()].filter(isAlive) as TaskLink[];
}

/**
 * Все записи связей, включая снятые.
 *
 * Нужны при заведении связи: пара «кто кого держит» уникальна в базе вместе с
 * надгробиями (`task_links_pair_uniq`), поэтому снятую связь возвращают
 * воскрешением её же записи. Показывать снятые связи, разумеется, нельзя —
 * для этого есть `linksOf`.
 */
export function linkRecordsOf(map: Map<string, SyncRecord>): TaskLink[] {
  return [...map.values()] as TaskLink[];
}

/** Карта пунктов чек-листа. */
export function selectChecklistMap(state: AppState): Map<string, SyncRecord> {
  return state.records.checklist_item;
}

/**
 * Пункты чек-листа по задачам, каждый список в своём порядке. Пункт — отдельная
 * запись, а не элемент массива внутри задачи: два пункта, добавленных
 * одновременно с двух устройств, при LWW по полю-массиву схлопнулись бы в один
 * (раздел 3.3 архитектуры).
 */
export function checklistByTask(map: Map<string, SyncRecord>): Map<string, ChecklistItem[]> {
  const grouped = new Map<string, ChecklistItem[]>();

  for (const record of map.values()) {
    if (!isAlive(record)) continue;

    const taskId = record['task_id'];
    if (typeof taskId !== 'string') continue;

    const known = grouped.get(taskId);
    if (known === undefined) grouped.set(taskId, [record as ChecklistItem]);
    else known.push(record as ChecklistItem);
  }

  for (const items of grouped.values()) items.sort(compareOrder);
  return grouped;
}

export function selectTask(state: AppState, id: string): Task | undefined {
  const record = state.records.task.get(id);
  return record !== undefined && isAlive(record) ? (record as Task) : undefined;
}

export function selectSettings(state: AppState): Settings | undefined {
  const [first] = state.records.settings.values();
  return first as Settings | undefined;
}

export function selectPendingKind(state: AppState): SyncStatus['kind'] {
  return state.status.kind;
}
